import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  StarIcon,
  PlayIcon,
  ChevronRightIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  CloudIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CursorArrowRaysIcon,
  SparklesIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  TrophyIcon,
  ClockIcon,
  CpuChipIcon,
  SignalIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import Navigation from './Navigation';
import Footer from './Footer';

const Homepage = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({
    projects: 0,
    clients: 0,
    satisfaction: 0,
    experience: 0
  });

  const targetStats = {
    projects: 150,
    clients: 80,
    satisfaction: 98,
    experience: 8
  };

  useEffect(() => {
    setIsVisible(true);

    // Animate counters
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setCounters({
        projects: Math.floor(targetStats.projects * progress),
        clients: Math.floor(targetStats.clients * progress),
        satisfaction: Math.floor(targetStats.satisfaction * progress),
        experience: Math.floor(targetStats.experience * progress)
      });

      if (step >= steps) {
        clearInterval(timer);
        setCounters(targetStats);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const services = [
    {
      icon: <GlobeAltIcon className="w-8 h-8" />,
      title: "Thiết kế Website",
      description: "Thiết kế website chuyên nghiệp, responsive, tối ưu SEO và trải nghiệm người dùng tốt nhất.",
      features: ["Responsive Design", "SEO Optimized", "Fast Loading", "Modern UI/UX"]
    },
    {
      icon: <DevicePhoneMobileIcon className="w-8 h-8" />,
      title: "Ứng dụng Mobile",
      description: "Phát triển ứng dụng mobile đa nền tảng iOS/Android với công nghệ hiện đại.",
      features: ["Cross Platform", "Native Performance", "Modern UI", "Secure"]
    },
    {
      icon: <CloudIcon className="w-8 h-8" />,
      title: "Hosting & Domain",
      description: "Dịch vụ hosting chất lượng cao, tên miền chuyên nghiệp và hỗ trợ 24/7.",
      features: ["99.9% Uptime", "24/7 Support", "SSL Certificate", "Backup Daily"]
    },
    {
      icon: <ChartBarIcon className="w-8 h-8" />,
      title: "Digital Marketing",
      description: "Chiến lược marketing hiệu quả, tăng doanh số và mở rộng thị trường.",
      features: ["SEO/SEM", "Social Media", "Content Marketing", "Analytics"]
    },
    {
      icon: <ShieldCheckIcon className="w-8 h-8" />,
      title: "Bảo mật & Backup",
      description: "Giải pháp bảo mật toàn diện và sao lưu dữ liệu an toàn cho doanh nghiệp.",
      features: ["Security Audit", "Data Backup", "SSL Protection", "Monitoring"]
    },
    {
      icon: <CpuChipIcon className="w-8 h-8" />,
      title: "AI & Automation",
      description: "Tích hợp trí tuệ nhân tạo và tự động hóa quy trình cho doanh nghiệp.",
      features: ["AI Integration", "Process Automation", "Data Analytics", "Smart Solutions"]
    }
  ];

  const testimonials = [
    {
      name: "Nguyễn Văn A",
      position: "CEO, ABC Company",
      content: "Smartera đã giúp chúng tôi xây dựng hệ thống website và ứng dụng mobile chuyên nghiệp. Đội ngũ rất tận tâm và chu đáo.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
    },
    {
      name: "Trần Thị B",
      position: "Marketing Director, XYZ Corp",
      content: "Dịch vụ digital marketing của Smartera đã giúp chúng tôi tăng 300% lượng khách hàng tiềm năng chỉ trong 6 tháng.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face"
    },
    {
      name: "Lê Văn C",
      position: "Founder, StartupDEF",
      content: "Từ ý tưởng đến sản phẩm hoàn thiện, Smartera đã đồng hành cùng chúng tôi trong suốt hành trình khởi nghiệp.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
    }
  ];

  const portfolioItems = [
    {
      title: "Nền tảng Thương mại Điện tử",
      category: "Phát triển Web",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop",
      description: "Nền tảng thương mại điện tử hiện đại với tính năng đầy đủ"
    },
    {
      title: "Ứng dụng Ngân hàng Di động",
      category: "Ứng dụng Mobile",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop",
      description: "Ứng dụng ngân hàng di động an toàn và tiện lợi"
    },
    {
      title: "Website Doanh nghiệp",
      category: "Thiết kế Web",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop",
      description: "Website doanh nghiệp chuyên nghiệp và hiện đại"
    }
  ];

  const teamMembers = [
    {
      name: "Võ Duy Thiện",
      role: "CEO & Founder",
      initials: "VT",
      description: "Với hơn 8 năm kinh nghiệm trong lĩnh vực công nghệ, dẫn dắt Smartera phát triển từ startup thành công ty uy tín trong ngành.",
      link: "https://voduythien.myresumes.work/"
    },
    {
      name: "Nguyễn Quốc Huy",
      role: "Senior Software Engineer",
      initials: "NH",
      description: "Chuyên gia phát triển phần mềm với kinh nghiệm sâu rộng trong các công nghệ hiện đại.",
      link: "https://nguyenquochuy.myresumes.work/"
    },
    {
      name: "Vũ Ngọc Thảo",
      role: "Project Manager",
      initials: "VT",
      description: "Chuyên gia quản lý dự án với kỹ năng tổ chức và điều phối xuất sắc.",
      link: "https://thaovu.myresumes.work/"
    }
  ];

  return (
    <div className="min-h-screen bg-dark-900 overflow-hidden">
      {/* Navigation */}
      <Navigation />

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="relative w-full max-w-4xl mx-4">
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors border border-cyan-500/30"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="relative pt-[56.25%] bg-dark-800 rounded-2xl overflow-hidden border border-cyan-500/20 shadow-neon">
              <iframe
                src="https://www.youtube.com/embed/BP1-CUXYXfg?si=MQb6Hv6MNsexAP-7&autoplay=1"
                title="Smartera Introduction Video"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section - Ocean High-Tech Theme */}
      <section className="relative pt-20 min-h-screen flex items-center overflow-hidden">
        {/* Animated Ocean Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-900 via-dark-800 to-dark-700">
          {/* Animated grid */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(0, 240, 255, 0.03) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(0, 240, 255, 0.03) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}></div>
          </div>

          {/* Glowing orbs */}
          <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/3 w-72 h-72 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>

          {/* Scan line effect */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent animate-scan-line"></div>
          </div>

          {/* Circuit pattern overlay */}
          <div className="absolute inset-0 hex-pattern opacity-30"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Tech badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 backdrop-blur-sm rounded-full text-cyan-400 text-sm mb-6 border border-cyan-500/20">
                <BoltIcon className="w-4 h-4" />
                <span className="font-mono">GIẢI PHÁP SỐ HÀNG ĐẦU VIỆT NAM</span>
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
              </div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">Digital</span>
                <br />
                <span className="text-white">Solutions</span>
                <br />
                <span className="text-ocean-400 text-3xl md:text-4xl font-light">cho doanh nghiệp</span>
              </h1>

              <p className="text-xl text-ocean-100/70 mb-8 leading-relaxed max-w-xl font-light">
                Chúng tôi chuyên phát triển website, ứng dụng mobile và cung cấp dịch vụ digital marketing
                giúp doanh nghiệp của bạn phát triển mạnh mẽ trong kỷ nguyên số.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link
                  to="/contact"
                  className="group relative overflow-hidden bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold shadow-glow-cyan hover:shadow-neon transition-all duration-300"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    Bắt đầu dự án
                    <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </Link>
                <button
                  onClick={() => setShowVideoModal(true)}
                  className="group bg-white/5 backdrop-blur-sm border border-cyan-500/30 text-cyan-400 px-8 py-4 rounded-xl font-semibold hover:bg-cyan-500/10 hover:border-cyan-400/50 transition-all duration-300"
                >
                  <div className="flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center mr-3 group-hover:bg-cyan-500/30 transition-colors">
                      <PlayIcon className="w-5 h-5" />
                    </div>
                    Xem video
                  </div>
                </button>
              </div>

              {/* Customer Portal Link */}
              <div className="p-5 bg-dark-800/50 backdrop-blur-sm rounded-2xl border border-cyan-500/10 hover:border-cyan-500/30 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                    <SignalIcon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-ocean-100/50 text-sm">Đã là khách hàng của Smartera?</p>
                    <Link
                      to="/login"
                      className="text-cyan-400 font-medium inline-flex items-center hover:text-cyan-300 transition-colors"
                    >
                      Đăng nhập Portal Khách hàng
                      <ChevronRightIcon className="w-5 h-5 ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Main Image with tech frame */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 rounded-3xl blur-xl"></div>
                <div className="relative rounded-3xl overflow-hidden border border-cyan-500/20 shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop"
                    alt="Digital Solutions"
                    className="w-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 via-transparent to-transparent"></div>
                </div>

                {/* Floating Card 1 */}
                <div className="absolute -bottom-6 -left-6 bg-dark-800/90 backdrop-blur-xl p-5 rounded-2xl border border-cyan-500/20 shadow-glow-cyan animate-float">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <CheckCircleIcon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white font-mono">150+</div>
                      <div className="text-sm text-cyan-400/70">Dự án thành công</div>
                    </div>
                  </div>
                </div>

                {/* Floating Card 2 */}
                <div className="absolute -top-6 -right-6 bg-dark-800/90 backdrop-blur-xl p-5 rounded-2xl border border-cyan-500/20 shadow-glow-ocean animate-float" style={{animationDelay: '0.5s'}}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                      <StarIcon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white font-mono">98%</div>
                      <div className="text-sm text-cyan-400/70">Khách hàng hài lòng</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-8 h-12 rounded-full border-2 border-cyan-500/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-cyan-400 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-dark-800/50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { value: counters.projects, suffix: '+', label: 'Dự án hoàn thành', icon: <RocketLaunchIcon className="w-6 h-6" /> },
              { value: counters.clients, suffix: '+', label: 'Khách hàng tin tưởng', icon: <UserGroupIcon className="w-6 h-6" /> },
              { value: counters.satisfaction, suffix: '%', label: 'Khách hàng hài lòng', icon: <TrophyIcon className="w-6 h-6" /> },
              { value: counters.experience, suffix: '+', label: 'Năm kinh nghiệm', icon: <ClockIcon className="w-6 h-6" /> }
            ].map((stat, index) => (
              <div key={index} className="group relative bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity"></div>
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center text-cyan-400 mb-4 border border-cyan-500/20">
                    {stat.icon}
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-2 font-mono">
                    {stat.value}{stat.suffix}
                  </div>
                  <div className="text-ocean-100/60 font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-800/50 to-dark-900"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 rounded-full text-cyan-400 text-sm font-mono mb-4 border border-cyan-500/20">
              <CpuChipIcon className="w-4 h-4" />
              <span>DỊCH VỤ CỦA CHÚNG TÔI</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Giải pháp công nghệ
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400"> toàn diện</span>
            </h2>
            <p className="text-xl text-ocean-100/60 max-w-3xl mx-auto">
              Chúng tôi cung cấp đầy đủ các dịch vụ công nghệ thông tin giúp doanh nghiệp
              của bạn chuyển đổi số một cách hiệu quả và toàn diện.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group relative bg-dark-800/50 backdrop-blur-sm p-8 rounded-2xl border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 hover:-translate-y-2"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity"></div>
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center text-cyan-400 mb-6 border border-cyan-500/20 group-hover:shadow-glow-cyan transition-shadow">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                  <p className="text-ocean-100/60 mb-5">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-ocean-100/70">
                        <CheckCircleIcon className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button className="text-cyan-400 font-semibold inline-flex items-center hover:text-cyan-300 transition-colors">
                    Tìm hiểu thêm
                    <ChevronRightIcon className="w-5 h-5 ml-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-dark-900"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 rounded-full text-purple-400 text-sm font-mono mb-4 border border-purple-500/20">
              <UserGroupIcon className="w-4 h-4" />
              <span>ĐỘI NGŨ CỦA CHÚNG TÔI</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Đội ngũ
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400"> chuyên gia</span>
            </h2>
            <p className="text-xl text-ocean-100/60 max-w-3xl mx-auto">
              Được thành lập từ năm 2017, Smartera tự hào có đội ngũ chuyên gia giàu kinh nghiệm,
              luôn sẵn sàng mang đến những giải pháp công nghệ tối ưu nhất.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="group relative bg-dark-800/50 backdrop-blur-sm p-8 rounded-2xl text-center border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 mx-auto mb-6 flex items-center justify-center shadow-glow-cyan group-hover:shadow-neon transition-shadow">
                  <span className="text-3xl font-bold text-white">{member.initials}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{member.name}</h3>
                <p className="text-cyan-400 font-semibold mb-4">{member.role}</p>
                <p className="text-ocean-100/60 text-sm mb-6">{member.description}</p>
                <a
                  href={member.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-cyan-400 font-medium hover:text-cyan-300 transition-colors"
                >
                  Xem hồ sơ
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>

          {/* Company History */}
          <div className="relative bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 p-1 rounded-3xl">
            <div className="bg-dark-900 rounded-[22px] p-8 md:p-12">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 rounded-full text-cyan-400 text-sm font-mono mb-4 border border-cyan-500/20">
                  <ClockIcon className="w-4 h-4" />
                  <span>TỪ NĂM 2017</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">Hành trình phát triển</h3>
                <p className="text-lg text-ocean-100/60 max-w-4xl mx-auto leading-relaxed">
                  Smartera đã không ngừng phát triển và khẳng định vị thế trong lĩnh vực công nghệ thông tin.
                  Với phương châm <span className="font-semibold text-cyan-400">"Chất lượng - Uy tín - Sáng tạo"</span>,
                  chúng tôi đã đồng hành cùng hàng trăm doanh nghiệp trong hành trình chuyển đổi số,
                  từ các startup nhỏ đến những tập đoàn lớn.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-24 relative">
        <div className="absolute inset-0 bg-dark-800/50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full text-emerald-400 text-sm font-mono mb-4 border border-emerald-500/20">
              <RocketLaunchIcon className="w-4 h-4" />
              <span>DỰ ÁN TIÊU BIỂU</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Portfolio
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400"> nổi bật</span>
            </h2>
            <p className="text-xl text-ocean-100/60">
              Một số dự án tiêu biểu mà chúng tôi đã thực hiện thành công
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {portfolioItems.map((item, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-2xl border border-cyan-500/10 group-hover:border-cyan-500/30 transition-all duration-300">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="inline-block px-3 py-1 bg-cyan-500/20 backdrop-blur-sm rounded-full text-cyan-400 text-sm font-medium mb-3 border border-cyan-500/20">
                      {item.category}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-ocean-100/70 text-sm">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold shadow-glow-ocean hover:shadow-neon transition-all duration-300"
            >
              Liên hệ để biết thêm
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-dark-900"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-full text-amber-400 text-sm font-mono mb-4 border border-amber-500/20">
              <StarIcon className="w-4 h-4" />
              <span>ĐÁNH GIÁ TỪ KHÁCH HÀNG</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Khách hàng
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400"> nói gì về chúng tôi</span>
            </h2>
            <p className="text-xl text-ocean-100/60">
              Phản hồi chân thực từ những khách hàng đã tin tưởng sử dụng dịch vụ
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-dark-800/50 backdrop-blur-sm p-8 rounded-2xl border border-cyan-500/10 hover:border-cyan-500/30 hover:-translate-y-2 transition-all duration-300"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 text-amber-400 fill-current" />
                  ))}
                </div>
                <p className="text-ocean-100/70 mb-6 italic leading-relaxed">"{testimonial.content}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover mr-4 border-2 border-cyan-500/30"
                  />
                  <div>
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-cyan-400/70">{testimonial.position}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/50 via-dark-800 to-purple-900/50"></div>

        {/* Animated elements */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-purple-500/10 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>

        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `linear-gradient(rgba(0, 240, 255, 0.05) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0, 240, 255, 0.05) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 backdrop-blur-sm rounded-full text-cyan-400 text-sm font-mono mb-6 border border-cyan-500/20">
            <RocketLaunchIcon className="w-4 h-4" />
            <span>BẮT ĐẦU NGAY HÔM NAY</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Sẵn sàng bắt đầu dự án của bạn?
          </h2>
          <p className="text-xl text-ocean-100/70 mb-10 max-w-2xl mx-auto">
            Hãy liên hệ với chúng tôi ngay hôm nay để được tư vấn miễn phí và nhận báo giá
            phù hợp nhất cho dự án của bạn.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="group bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold shadow-glow-cyan hover:shadow-neon transition-all duration-300"
            >
              <span className="flex items-center justify-center">
                Liên hệ ngay
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
            <Link
              to="/register"
              className="group bg-white/5 backdrop-blur-sm border border-cyan-500/30 text-cyan-400 px-8 py-4 rounded-xl font-semibold hover:bg-cyan-500/10 transition-all duration-300"
            >
              Đăng ký tài khoản
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Custom styles for animations */}
      <style jsx>{`
        @keyframes scan-line {
          0% { top: 0; }
          100% { top: 100%; }
        }
        .animate-scan-line {
          animation: scan-line 8s linear infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Homepage;
